import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../components/Button';
import Checkbox from '../../../components/Checkbox/Checkbox';
import Loading from '../../../components/Loading';
import ModalConfirmation from '../../../components/Modal/ModalConfirmation';
import { API_NOTIFICATION } from '../../../helpers/api';
import { setNotification } from '../../../helpers/notification';
import {
  getNotificationSettingsRequest,
  getNotificationSettingsSucceeded,
} from '../../../redux/actions/notification';

const NotificationSettings = ({ t }) => {
  const dispatch = useDispatch();
  const { organizationId } = useSelector(
    (state) => state.authorization.user.organization
  );
  const { data, settingsLoading } = useSelector(
    (state) => state.notification.settings
  );

  const initialData = {
    isBilateralAgreementApprovalSMS: false,
    isBilateralAgreementApprovalEmail: false,
    isBilateralAgreementPendingApprovalSMS: false,
    isBilateralAgreementPendingApprovalEmail: false,
    isBilateralAgreementCancelSMS: false,
    isBilateralAgreementCancelEmail: false,
    isObjectionSMS: false,
    isObjectionEmail: false,
    isProcessChangesSMS: false,
    isProcessChangesEmail: false,
    isDocumentRepealSMS: false,
    isDocumentRepealEmail: false,
    isRepealInformationSMS: false,
    isRepealInformationEmail: false,
    isDocumentCancelSMS: false,
    isDocumentCancelEmail: false,
    isOrganizeMarketMatchUpInformationSMS: false,
    isOrganizeMarketMatchUpInformationEmail: false,
  };

  const [displayedData, setDisplayedData] = useState({ ...initialData });
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [modalConfirmationDetails, setModalConfirmationDetails] = useState({});
  const [loadingRequest, setLoadingRequest] = useState(false);

  useEffect(() => {
    dispatch(getNotificationSettingsRequest(organizationId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data.id) {
      setDisplayedData(data);
    }
  }, [data]);

  const handleCheckboxChange = (e) => {
    const { name } = e.target;
    setDisplayedData((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  const handleSetModalConfirmationDetails = (type) => {
    setShowModalConfirmation(true);
    switch (type) {
      case 'DISCARD':
        setModalConfirmationDetails({
          header: t('pages.default.notifications.discardChanges.header'),
          body: t('pages.default.notifications.discardChanges.body'),
          approve: handleDiscardClick,
        });
        break;
      case 'SAVE':
        setModalConfirmationDetails({
          header: t('pages.default.notifications.saveChanges.header'),
          body: t('pages.default.notifications.saveChanges.body'),
          approve: handleSaveClick,
        });
        break;
      default:
        break;
    }
  };

  const handleDiscardClick = () => {
    setDisplayedData(
      data.id
        ? data
        : {
            ...initialData,
          }
    );
    setShowModalConfirmation(false);
  };

  const handleSaveClick = async () => {
    setLoadingRequest(true);
    let res = null;
    try {
      if (data.id) {
        res = await API_NOTIFICATION('notification/settings', 'PUT', {
          id: data.id,
          organizationId,
          ...displayedData,
        });
      } else {
        res = await API_NOTIFICATION('notification/settings', 'POST', {
          organizationId,
          ...displayedData,
        });
      }
      dispatch(getNotificationSettingsSucceeded({ ...res.data }));
      setNotification(
        'success',
        t('pages.default.notifications.updateSuccess')
      );
      setLoadingRequest(false);
      setShowModalConfirmation(false);
    } catch (err) {
      setLoadingRequest(false);
      setShowModalConfirmation(false);
      err.response && setNotification('error', err.response.data.message);
    }
  };

  return (
    <>
      <h5 className='km__u--text-has-emphasis'>
        {t('pages.default.notifications.settingsTitle')}
      </h5>
      <div className='km__p--card'>
        <div className='km__p--notification-settings-table-container'>
          {!settingsLoading && (
            <div className='km__c--table'>
              <div className='km__c--table-container'>
                <table>
                  <thead>
                    <tr>
                      <th>
                        {t(
                          'pages.default.notifications.settingsTable.notifications'
                        )}
                      </th>
                      <th>
                        {t('pages.default.notifications.settingsTable.sms')}
                      </th>
                      <th>
                        {t('pages.default.notifications.settingsTable.ePosta')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td id='settings-name-isBilateralAgreementApprovalSMS'>
                        {t(
                          'pages.default.notifications.settingsTable.rows.bilateralAgreementAprroval'
                        )}
                      </td>
                      <td>
                        <Checkbox
                          name='isBilateralAgreementApprovalSMS'
                          checked={
                            displayedData.isBilateralAgreementApprovalSMS
                          }
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isBilateralAgreementApprovalSMS'
                        />
                      </td>
                      <td>
                        <Checkbox
                          name='isBilateralAgreementApprovalEmail'
                          checked={
                            displayedData.isBilateralAgreementApprovalEmail
                          }
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isBilateralAgreementApprovalEmail'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td id='settings-name-isBilateralAgreementPendingApprovalSMS'>
                        {t(
                          'pages.default.notifications.settingsTable.rows.bilateralAgreementPendingAprroval'
                        )}
                      </td>
                      <td>
                        <Checkbox
                          name='isBilateralAgreementPendingApprovalSMS'
                          checked={
                            displayedData.isBilateralAgreementPendingApprovalSMS
                          }
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isBilateralAgreementPendingApprovalSMS'
                        />
                      </td>
                      <td>
                        <Checkbox
                          name='isBilateralAgreementPendingApprovalEmail'
                          checked={
                            displayedData.isBilateralAgreementPendingApprovalEmail
                          }
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isBilateralAgreementPendingApprovalEmail'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td id='settings-name-isBilateralAgreementCancelSMS'>
                        {t(
                          'pages.default.notifications.settingsTable.rows.bilateralAgreementCancel'
                        )}
                      </td>
                      <td>
                        <Checkbox
                          name='isBilateralAgreementCancelSMS'
                          checked={displayedData.isBilateralAgreementCancelSMS}
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isBilateralAgreementCancelSMS'
                        />
                      </td>
                      <td>
                        <Checkbox
                          name='isBilateralAgreementCancelEmail'
                          checked={
                            displayedData.isBilateralAgreementCancelEmail
                          }
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isBilateralAgreementCancelEmail'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td id='settings-name-isObjectionSMS'>
                        {t(
                          'pages.default.notifications.settingsTable.rows.objection'
                        )}
                      </td>
                      <td>
                        <Checkbox
                          name='isObjectionSMS'
                          checked={displayedData.isObjectionSMS}
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isObjectionSMS'
                        />
                      </td>
                      <td>
                        <Checkbox
                          name='isObjectionEmail'
                          checked={displayedData.isObjectionEmail}
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isObjectionEmail'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td id='settings-name-isProcessChangesSMS'>
                        {t(
                          'pages.default.notifications.settingsTable.rows.processChanges'
                        )}
                      </td>
                      <td>
                        <Checkbox
                          name='isProcessChangesSMS'
                          checked={displayedData.isProcessChangesSMS}
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isProcessChangesSMS'
                        />
                      </td>
                      <td>
                        <Checkbox
                          name='isProcessChangesEmail'
                          checked={displayedData.isProcessChangesEmail}
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isProcessChangesEmail'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td id='settings-name-isDocumentRepealSMS'>
                        {t(
                          'pages.default.notifications.settingsTable.rows.certificateRepeal'
                        )}
                      </td>
                      <td>
                        <Checkbox
                          name='isDocumentRepealSMS'
                          checked={displayedData.isDocumentRepealSMS}
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isDocumentRepealSMS'
                        />
                      </td>
                      <td>
                        <Checkbox
                          name='isDocumentRepealEmail'
                          checked={displayedData.isDocumentRepealEmail}
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isDocumentRepealEmail'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td id='settings-name-isRepealInformationSMS'>
                        {t(
                          'pages.default.notifications.settingsTable.rows.repealInforming'
                        )}
                      </td>
                      <td>
                        <Checkbox
                          name='isRepealInformationSMS'
                          checked={displayedData.isRepealInformationSMS}
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isRepealInformationSMS'
                        />
                      </td>
                      <td>
                        <Checkbox
                          name='isRepealInformationEmail'
                          checked={displayedData.isRepealInformationEmail}
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isRepealInformationEmail'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td id='settings-name-certificateCancel'>
                        {t(
                          'pages.default.notifications.settingsTable.rows.certificateCancel'
                        )}
                      </td>
                      <td>
                        <Checkbox
                          name='isDocumentCancelSMS'
                          checked={displayedData.isDocumentCancelSMS}
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isDocumentCancelSMS'
                        />
                      </td>
                      <td>
                        <Checkbox
                          name='isDocumentCancelEmail'
                          checked={displayedData.isDocumentCancelEmail}
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isDocumentCancelEmail'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td id='settings-name-isOrganizeMarketMatchUpInformationSMS'>
                        {t(
                          'pages.default.notifications.settingsTable.rows.organizedMarketMatchInfo'
                        )}
                      </td>
                      <td>
                        <Checkbox
                          name='isOrganizeMarketMatchUpInformationSMS'
                          checked={
                            displayedData.isOrganizeMarketMatchUpInformationSMS
                          }
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isOrganizeMarketMatchUpInformationSMS'
                        />
                      </td>
                      <td>
                        <Checkbox
                          name='isOrganizeMarketMatchUpInformationEmail'
                          checked={
                            displayedData.isOrganizeMarketMatchUpInformationEmail
                          }
                          onChange={handleCheckboxChange}
                          id='settings-checkbox-isOrganizeMarketMatchUpInformationEmail'
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {settingsLoading && <Loading />}
        </div>
        <div className='km__p--notification-settings-button-group'>
          <Button
            type='destructive'
            label={t('ui.default.discard')}
            onClick={() => handleSetModalConfirmationDetails('DISCARD')}
            disabled={loadingRequest}
            id='settings-button-discard'
          />
          <Button
            label={t('ui.default.save')}
            onClick={() => handleSetModalConfirmationDetails('SAVE')}
            disabled={loadingRequest}
            id='settings-button-save'
          />
        </div>
      </div>
      <ModalConfirmation
        isOpen={showModalConfirmation}
        toggle={() => setShowModalConfirmation(false)}
        {...modalConfirmationDetails}
        loading={loadingRequest}
      />
    </>
  );
};

export default NotificationSettings;
